import { TASK_CARD_BRAND_LOGO } from '@/app/[locale]/tasks/components/TaskCard/taskCard.constants'
import type { TaskCardDemoRow } from '@/app/[locale]/tasks/components/TasksTaskCards/tasksTaskCards.constants'
import type { WithTaskGroup } from '@/features/tasks/api/taskGroupsApi'
import {
  type MyActiveTaskProgress,
  pickVideoUrl
} from '@/features/tasks/api/watchTaskApi'
import { applyTaskGroupToRow } from '@/features/tasks/mappers/applyTaskGroupToRow'
import {
  firstNonEmptyTrimmed,
  hoursLeftFromAvailableUntil,
  mapAvailableTaskCardToTaskRow,
  mapTaskCardSocialPlatform
} from '@/features/tasks/mappers/mapAvailableTaskCardToTaskRow'
import { pickTaskCoverUrl } from '@/features/tasks/mappers/pickTaskCoverUrl'
import { readActionPlatforms } from '@/features/tasks/mappers/readActionPlatforms'
import {
  getTaskCardDemoTitle,
  mapTaskTypeToCta,
  type TaskCardCta
} from '@/features/tasks/taskCardCta'
import { resolveActiveTaskDeadlineIso } from '@/features/tasks/utils/activeTaskState'

function hashToCoverIndex(taskId: string): 0 | 1 | 2 | 3 {
  let h = 0
  for (let i = 0; i < taskId.length; i++) {
    h = (h + taskId.charCodeAt(i)) % 997
  }
  return (h % 4) as 0 | 1 | 2 | 3
}

export type MapTaskProgressToTaskRowOptions = {
  listVariant?: 'active' | 'closed'
}

export function mapTaskProgressToTaskRow(
  p: MyActiveTaskProgress & WithTaskGroup,
  options?: MapTaskProgressToTaskRowOptions
): TaskCardDemoRow {
  const row = mapProgressToRow(p, options)
  const group = p.task_group
  if (!group) {
    return row
  }
  return applyTaskGroupToRow(
    row,
    mapAvailableTaskCardToTaskRow(group.brief),
    group.platforms.map(platform =>
      platform.progress
        ? mapProgressToRow(platform.progress, options)
        : mapAvailableTaskCardToTaskRow(platform.task)
    )
  )
}

function mapProgressToRow(
  p: MyActiveTaskProgress,
  options?: MapTaskProgressToTaskRowOptions
): TaskCardDemoRow {
  const cta: TaskCardCta = mapTaskTypeToCta(p.type)
  const displayTitle = firstNonEmptyTrimmed(p.title, p.campaign_name)
  const brandName = (p.brand_name ?? '').trim()
  const brandLogoUrl = (p.brand_logo_url ?? '').trim()
  const claimed = Math.max(0, Math.floor(Number(p.claimed_count) || 0))
  const volume = Math.max(0, Math.floor(Number(p.volume) || 0))
  const participantLimit =
    volume > 0 ? Math.max(1, volume) : Math.max(1, claimed + 1)
  const participantCurrent = claimed
  const coverUrl =
    pickTaskCoverUrl({
      thumbnailUrl: p.thumbnail_url,
      instruction: p.instruction ?? undefined,
      preferThumbnail: cta === 'watch'
    }) ?? undefined
  const watchVideoUrl =
    pickVideoUrl(p) ?? pickVideoUrl(p.instruction ?? undefined) ?? undefined
  const fromClosedTaskList = options?.listVariant === 'closed'
  const stateRaw = (p.state ?? '').trim().toLowerCase()
  const rewardClaimedFlag = p.reward_claimed === true
  const closedWatchRewardClaimed =
    fromClosedTaskList &&
    cta === 'watch' &&
    (rewardClaimedFlag ||
      stateRaw === 'success' ||
      stateRaw === 'completed' ||
      stateRaw === 'done')
  const stateIsFailed =
    stateRaw === 'failed' ||
    stateRaw === 'failure' ||
    stateRaw === 'error' ||
    stateRaw === 'unsuccessful'
  const stateIsSuccessLike =
    stateRaw === 'success' || stateRaw === 'completed' || stateRaw === 'done'
  let watchProgressState: 'in_progress' | 'success' | 'failed' | undefined
  if (stateRaw === 'in_progress') {
    watchProgressState = 'in_progress'
  } else if (stateIsSuccessLike) {
    watchProgressState = 'success'
  } else if (stateIsFailed) {
    watchProgressState = 'failed'
  } else {
    watchProgressState = undefined
  }
  const watchFailureReason =
    stateIsFailed &&
    typeof p.failure_reason === 'string' &&
    p.failure_reason.trim().length > 0
      ? p.failure_reason.trim()
      : undefined

  return {
    id: p.task_id,
    taskProgressId: p.task_progress_id ?? p.id,
    rewardPoints: Math.max(0, Math.floor(Number(p.fan_reward) || 0)),
    hoursLeft: hoursLeftFromAvailableUntil(
      resolveActiveTaskDeadlineIso(p, cta)
    ),
    coverI: hashToCoverIndex(p.task_id),
    coverUrl,
    participantCurrent,
    participantLimit,
    popularityScore: claimed,
    social: mapTaskCardSocialPlatform(p.platform),
    cta,
    reserved: false,
    title: getTaskCardDemoTitle(cta),
    apiTitle: displayTitle || undefined,
    apiBrandName: brandName || undefined,
    apiBrandLogoUrl: brandLogoUrl ? brandLogoUrl : TASK_CARD_BRAND_LOGO,
    apiDescription: p.description?.trim() || undefined,
    apiBrandResources: p.brand_resources,
    watchVideoUrl,
    fromClosedTaskList,
    closedWatchRewardClaimed,
    watchProgressState,
    watchFailureReason,
    actionPlatforms:
      cta === 'action' ? readActionPlatforms(p.action_details) : undefined
  }
}
